import { useState, useEffect, useMemo } from "react";
import { Property } from "../../../../models/Property";
import { fitImageForPDF } from "./fit-images-for-pdf";
import PropertyDocument from "../pdf-gen/PropertyDocument";

export type ResizerStatus  = "idle" | "inProgress" | "success" | "error";

export type ResizerState =
  | { status: 'idle' }
  | { status: 'inProgress' }
  | { status: 'success'; primary: string; images: string[] }
  | { status: 'error'; error: unknown };

export default function usePropertyDocument(property: Property) {
  const [state, setState] = useState<ResizerState>({ status: 'idle' });

  useEffect(() => {
    if (!property) return;

    const controller = new AbortController();
    const { signal } = controller;

    async function resizeAll() {
      setState({ status: 'inProgress' });

      try {
        const primary = await fitImageForPDF(property.primaryImage, 1600, 1200, { signal });
        const general = await Promise.all(
          property.images
            .filter(img => img !== property.primaryImage)
            .map(img => fitImageForPDF(img, 1600, 1200, { signal }))
        );

        if (signal.aborted) return;

        setState({ status: 'success', primary, images: general });
      } catch (error) {
        if (signal.aborted) return;
        setState({ status: 'error', error });
        console.error('Failed to resize images for PDF:', error);
      }
    }

    resizeAll();

    return () => controller.abort();
  }, [property]);

  const doc = useMemo(() => {
    if (state.status !== 'success') return undefined;

    const doc = (
      <PropertyDocument
        property={property}
        resizedPrimaryImage={state.primary}
        resizedImages={state.images}
      />
    );

    return doc;
    
  }, [state, property]);

  return {
    resizerState: state.status,
    propertyDocument: doc,
    error: state.status === 'error' ? state.error : null,
  } as const;
}